import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils/cn'
import { Logo } from './Logo'
import { LanguageToggle } from './LanguageToggle'
import { UserMenu } from './UserMenu'
import { useAuth } from '@/hooks/useAuth'
import { useTranslation } from 'react-i18next'

/* ------------------------------------------------------------------ */
/*  TopBar — desktop navigation header                                 */
/*  Logo left, primary nav center, auth/dashboard right               */
/*  Sticky with blurred backdrop on scroll                             */
/*  See ARCHITECTURE.md §17                                            */
/* ------------------------------------------------------------------ */

export interface TopBarProps {
  /** Current pathname for active-link highlighting */
  currentPath?: string
}

const NAV_ITEMS = [
  { href: '/tjanster', labelKey: 'nav.services' },
  { href: '/hur-det-fungerar', labelKey: 'nav.howItWorks' },
  { href: '/for-leverantorer', labelKey: 'nav.forProviders' },
  { href: '/priser', labelKey: 'nav.pricing' },
] as const

export function TopBar({ currentPath = '/' }: TopBarProps) {
  const { t } = useTranslation()
  const { isAuthenticated, isLoading } = useAuth()
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 8)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <header
      role="banner"
      className={cn(
        'sticky top-0 z-40 w-full transition-all duration-200',
        scrolled
          ? 'border-b border-neutral-200 bg-neutral-0/80 shadow-xs backdrop-blur-lg'
          : 'bg-neutral-0',
      )}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 pt-[env(safe-area-inset-top,0px)] sm:px-6 lg:px-8">
        {/* Left: Logo */}
        <Logo currentPath={currentPath} size="md" />

        {/* Center: Navigation */}
        <nav className="hidden items-center gap-1 md:flex" aria-label="Huvudnavigation">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                'rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
                currentPath === item.href || currentPath?.startsWith(item.href + '/')
                  ? 'text-primary-700'
                  : 'text-neutral-700 hover:bg-neutral-50 hover:text-neutral-900',
              )}
              aria-current={currentPath === item.href ? 'page' : undefined}
            >
              {t(item.labelKey)}
            </Link>
          ))}
        </nav>

        {/* Right: language toggle (always visible) + auth actions (desktop) */}
        <div className="flex items-center gap-2">
          <LanguageToggle />

          <div className="hidden items-center gap-2 md:flex">
            {isAuthenticated ? (
              <UserMenu />
            ) : isLoading ? null : (
              <>
            <Link
              to="/logga-in"
              className={cn(
                'inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-medium',
                'text-neutral-700 hover:bg-neutral-50 hover:text-neutral-900',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
                'transition-colors',
              )}
            >
              {t('nav.login')}
            </Link>
            <Link
              to="/skapa-konto"
              className={cn(
                'inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium',
                'bg-primary-600 text-white hover:bg-primary-700',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2',
                'transition-colors',
              )}
            >
              {t('nav.signup')}
            </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}

TopBar.displayName = 'TopBar'
